import { utils, LitElement, html } from '@hh/global.js'

class HHProductEngraving extends LitElement {
  static properties = {
    imageRendering: {
      type: Boolean,
      attribute: 'image-rendering',
    },
  }

  constructor() {
    super()

    this.modal = utils.one('#engraving-modal')
    this.input = utils.one('input[type="text"]', this)
    this.dropdown = utils.one(
      '.js-dropdown-input.is-engraving-dropdown',
      document
    )
    if (this.modal) {
      this.modalInput = utils.one(
        '.js-engraving-modal-text input[type="text"]',
        this.modal
      )
      this.modalDropdown = utils.one(
        '.js-engraving-modal-dropdown-input',
        this.modal
      )
      this.modalConfirm = utils.one('.js-engraving-modal-confirm', this.modal)
      this.updateEngraving = utils.one('.js-product-engraving-update', document)
    }

    this.engravingImage = utils
      .all('.img img', this.gallery)
      .find((img) =>
        img.getAttribute('alt').toLowerCase().includes('engraving')
      )
    this.engravingImageWrapper = this.engravingImage?.closest(
      '.js-thumbnail-wrapper'
    )

    this.gallery = utils.one('.js-product-gallery')
    this.onInputCharacters = this.onInputCharacters.bind(this)
    if (this.modal) {
      this.onEngravingDropdownChange = this.onEngravingDropdownChange.bind(this)
      this.onInputSync = this.onInputSync.bind(this)
    }
  }

  connectedCallback() {
    super.connectedCallback()

    this.input?.addEventListener('input', this.onInputCharacters)
    if (this.modal) {
      this.input?.addEventListener('input', () =>
        this.onInputSync(this.input, this.modalInput)
      )
      this.dropdown?.addEventListener('change', this.onEngravingDropdownChange)
      this.modalInput?.addEventListener('input', this.onInputCharacters)
      this.modalInput?.addEventListener('input', () =>
        this.onInputSync(this.modalInput, this.input)
      )
      this.modalDropdown?.addEventListener(
        'change',
        this.onEngravingDropdownChange
      )
      this.modalConfirm?.addEventListener('click', () =>
        this._exitEngravingModal()
      )
      this.updateEngraving?.addEventListener('click', () =>
        this._openEngravingModal()
      )
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.input?.removeEventListener('input', this.onInputCharacters)
    if (this.modal) {
      this.input?.removeEventListener('input', () =>
        this.onInputSync(this.input, this.modalInput)
      )
      this.dropdown?.removeEventListener(
        'change',
        this.onEngravingDropdownChange
      )
      this.modalInput?.removeEventListener('input', this.onInputCharacters)
      this.modalInput?.removeEventListener('input', () =>
        this.onInputSync(this.modalInput, this.input)
      )
      this.modalDropdown?.removeEventListener(
        'change',
        this.onEngravingDropdownChange
      )
      this.modalConfirm?.removeEventListener('click', () =>
        this._exitEngravingModal()
      )
      this.updateEngraving?.removeEventListener('click', () =>
        this._openEngravingModal()
      )
    }
  }

  onInputCharacters(event) {
    event.preventDefault()

    const text = event.target.value.trim().replace(/\s+/g, ' ')
    this._checkErrors(text)
    this._onImageRendering(text)
    this._updateEngravingVisibility()
  }

  onInputSync(source, target) {
    if (target) {
      target.value = source.value
    }
  }

  onEngravingDropdownChange(event) {
    if (event.isProgrammatic) {
      return
    }

    const selectedOption = event.detail?.value

    if (this.modalInput && this.input) {
      this._onImageRendering('')
      this.modalInput.value = ''
      this.input.value = ''
    }

    if (
      !this.modal.open &&
      selectedOption &&
      selectedOption !== 'No Engraving'
    ) {
      this.modalDropdown._selected = selectedOption
      this.modalDropdown.requestUpdate?.()
      this._openEngravingModal()
    }

    if (this.modal.open) {
      const dropdownInput = utils.one(
        'input[name="options[Engraving]"]',
        this.dropdown
      )
      this.dropdown._selected = selectedOption
      this.dropdown.requestUpdate?.()

      if (dropdownInput) {
        dropdownInput.value = this.dropdown._selected
        const programmaticEvent = new Event('change', {
          bubbles: true,
          composed: true,
        })
        programmaticEvent.isProgrammatic = true
        dropdownInput.dispatchEvent(programmaticEvent)
      }

      if (selectedOption === 'No Engraving') {
        this.modal.open = false
      }
    }

    this._updateMaxLength()
    this._updateEngravingVisibility()
  }

  _openEngravingModal() {
    if (this.modal) {
      this._updateMaxLength()
      this.modal.open = true
    }
  }

  _exitEngravingModal() {
    const text = this.modalInput.value.trim().replace(/\s+/g, ' ')

    this._checkErrors(text)

    if (
      utils
        .one('.js-error-engraving-required', this.modal)
        .classList.contains('hidden')
    ) {
      this.modal.open = false
    }

    this._updateEngravingVisibility()
  }

  _checkErrors(text) {
    const errorMsgs = document.querySelectorAll('.js-error-engraving-required')

    if (errorMsgs) {
      errorMsgs.forEach((item) => {
        item.classList[text !== '' ? 'add' : 'remove']('hidden')
      })
    }
  }

  _updateMaxLength() {
    const selectedOption = this.dropdown?._selected || 'No Engraving'
    const numbers = selectedOption.match(/\d+/g)?.map(Number)
    const maxLength = numbers && numbers.length > 0 ? Math.max(...numbers) : 0

    if (this.modalInput) {
      this.modalInput.setAttribute('maxlength', maxLength)
    }
    if (this.input) {
      this.input.setAttribute('maxlength', maxLength)
    }
  }

  _updateEngravingVisibility() {
    const isNoEngravingSelected =
      this.modalDropdown?._selected === 'No Engraving' ||
      this.dropdown?._selected === 'No Engraving'
    const isInputEmpty =
      !this.input?.value.trim() && !this.modalInput?.value.trim()

    // Update visibility for the "update" button
    if (this.updateEngraving) {
      if (isNoEngravingSelected && isInputEmpty) {
        this.updateEngraving.classList.add('hidden')
      } else {
        this.updateEngraving.classList.remove('hidden')
      }
    }

    // Update visibility for the "add" and "edit" spans
    const addSpan = this.updateEngraving?.querySelector(
      '.js-product-engraving-add'
    )
    const editSpan = this.updateEngraving?.querySelector(
      '.js-product-engraving-edit'
    )

    if (addSpan) {
      if (isInputEmpty) {
        addSpan.classList.remove('hidden')
      } else {
        addSpan.classList.add('hidden')
      }
    }

    if (editSpan) {
      if (isInputEmpty) {
        editSpan.classList.add('hidden')
      } else {
        editSpan.classList.remove('hidden')
      }
    }
  }

  _onImageRendering(text) {
    if (!this.imageRendering) return

    // Rendering for product gallery
    this.engravingImageIndex = utils
      .all('.img img', this.gallery)
      .findIndex((img) =>
        img.getAttribute('alt').toLowerCase().includes('engraving')
      )

    if (this.engravingImageIndex !== -1) {
      const textItem = utils.one(
        '.js-engraving-viewbox text',
        this.engravingImageWrapper
      )
      this._renderTextOnElement(text, textItem, this.dataset)
    }

    // Rendering for engraving modal
    if (this.modal) {
      const modalTextItem = utils.one('.js-engraving-viewbox text', this.modal)
      if (modalTextItem) {
        this._renderTextOnElement(text, modalTextItem, this.dataset, true)
      }
    }
  }

  _renderTextOnElement(text, element, dataset, modalView = false) {
    if (!element) return

    const monogramFrom = Number(dataset.monogramFrom) || 0
    const maxLength = Number(this.input.getAttribute('maxlength')) || 0
    const additional_monogram = monogramFrom > 0 && maxLength >= monogramFrom

    let fontSize = dataset.fontSize

    if (text !== '' && !additional_monogram) {
      const textPositionX = modalView
        ? Number(this.modal.dataset.positionX)
        : Number(dataset.positionX)
      const textPositionY = modalView
        ? Number(this.modal.dataset.positionY)
        : Number(dataset.positionY)

      element.setAttribute('x', textPositionX)
      element.setAttribute('y', textPositionY)

      const uppercaseCount = (text.match(/[A-Z]/g) || []).length
      const lowercaseCount = (text.match(/[a-z]/g) || []).length

      // Apply rules based on uppercase and lowercase counts
      if (uppercaseCount === 1 && lowercaseCount <= 1) {
        if (text === 'O' || text === 'D' || text === 'Q' || text === 'Z') {
          element.setAttribute('x', textPositionX - 0.75)
        } else {
          element.setAttribute('x', textPositionX - 1.25)
        }
      } else if (uppercaseCount === 0 && lowercaseCount > 0) {
        element.setAttribute('x', textPositionX + 0.4)
      } else if (uppercaseCount >= 2) {
        fontSize =
          uppercaseCount === 2 && lowercaseCount === 1
            ? dataset.fontSize * 0.75
            : dataset.fontSize * 0.63
      }

      element.setAttribute('font-size', fontSize)
      element.innerHTML =
        '<tspan>&nbsp;</tspan>' + text + '<tspan>&nbsp;</tspan>'
    } else {
      // Reset the element if text is empty
      element.removeAttribute('x')
      element.removeAttribute('y')
      element.removeAttribute('font-size')
      element.innerHTML = ''
    }
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-product-engraving', HHProductEngraving)
